// Shared waveform envelope rendering (used by ChopStation + DrumLoopEditor).
// The look: per-pixel min/max envelope traced as one closed polygon and
// filled — a continuous crisp shape, no per-column seams.

// Downsample to a min/max envelope. High bucket count so deep zooms still
// resolve detail; raw data is kept for zoom levels finer than the buckets.
export function computePeaks(buffer, buckets = 32768) {
  const data = buffer.getChannelData(0);
  const step = Math.max(1, Math.floor(data.length / buckets));
  const stride = Math.max(1, Math.floor(step / 64));
  const mins = new Float32Array(buckets);
  const maxs = new Float32Array(buckets);
  let peak = 0;
  for (let i = 0; i < buckets; i++) {
    const start = i * step;
    const end = Math.min(start + step, data.length);
    let mn = Infinity;
    let mx = -Infinity;
    for (let j = start; j < end; j += stride) {
      const v = data[j];
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    }
    if (mn === Infinity) { mn = 0; mx = 0; }
    mins[i] = mn;
    maxs[i] = mx;
    const a = Math.max(Math.abs(mn), Math.abs(mx));
    if (a > peak) peak = a;
  }
  if (peak > 0) {
    for (let i = 0; i < buckets; i++) {
      mins[i] /= peak;
      maxs[i] /= peak;
    }
  }
  // The typed arrays are non-enumerable: this object flows through React
  // props, and dev-mode instrumentation (React performance tracks,
  // Agentation) serializes enumerable props on every commit — walking
  // millions of Float32Array entries froze the app for seconds. Direct
  // property access (peaks.mins etc.) is unaffected.
  const peaksObj = { norm: peak > 0 ? 1 / peak : 1 };
  Object.defineProperties(peaksObj, {
    mins: { value: mins, enumerable: false },
    maxs: { value: maxs, enumerable: false },
    data: { value: data, enumerable: false }
  });
  return peaksObj;
}

// Min/max over a fractional [f0, f1) slice. When zoomed so deep that the
// slice is finer than the precomputed buckets, scan the raw samples instead
// (the window is tiny at that point, so it stays cheap).
export function rangeMinMax(peaks, f0, f1) {
  const len = peaks.mins.length;
  if ((f1 - f0) * len < 2 && peaks.data) {
    const n = peaks.data.length;
    const j0 = Math.max(0, Math.floor(f0 * n));
    const j1 = Math.min(n, Math.max(j0 + 1, Math.ceil(f1 * n)));
    let mn = 0;
    let mx = 0;
    for (let j = j0; j < j1; j++) {
      const v = peaks.data[j];
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    }
    return [mn * peaks.norm, mx * peaks.norm];
  }
  const i0 = Math.max(0, Math.floor(f0 * len));
  const i1 = Math.min(len, Math.max(i0 + 1, Math.ceil(f1 * len)));
  let mn = 0;
  let mx = 0;
  for (let i = i0; i < i1; i++) {
    if (peaks.mins[i] < mn) mn = peaks.mins[i];
    if (peaks.maxs[i] > mx) mx = peaks.maxs[i];
  }
  return [mn, mx];
}

export function sizeCanvas(canvas, cssW, cssH) {
  const dpr = window.devicePixelRatio || 1;
  const w = Math.round(cssW * dpr);
  const h = Math.round(cssH * dpr);
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

// Trace the envelope of audio time [tA, tB] across pixel span [x0, x1] as ONE
// closed polygon and fill it.
export function fillEnvelopeShape(ctx, peaks, duration, tA, tB, x0, x1, cssH, color) {
  const w = Math.max(1, Math.round(x1 - x0));
  const mid = cssH / 2;
  const amp = (cssH - 6) / 2;
  const tops = new Float32Array(w + 1);
  const bots = new Float32Array(w + 1);
  for (let i = 0; i <= w; i++) {
    const f0 = (tA + (Math.max(0, i - 0.5) / w) * (tB - tA)) / duration;
    const f1 = (tA + (Math.min(w, i + 0.5) / w) * (tB - tA)) / duration;
    const [mn, mx] = rangeMinMax(peaks, f0, f1);
    let yT = mid - mx * amp;
    let yB = mid - mn * amp;
    if (yB - yT < 1) { yT = mid - 0.5; yB = mid + 0.5; }
    tops[i] = yT;
    bots[i] = yB;
  }
  ctx.beginPath();
  ctx.moveTo(x0, tops[0]);
  for (let i = 1; i <= w; i++) ctx.lineTo(x0 + i, tops[i]);
  for (let i = w; i >= 0; i--) ctx.lineTo(x0 + i, bots[i]);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}
